import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaLock, FaCreditCard, FaArrowLeft, FaCheck, FaStore, FaTruck, FaShieldAlt } from 'react-icons/fa';
import { useCart } from '../contexts/CartContext';
import { partsStoreService } from '../services/partsStoreService';
import { authorizeNetDirect } from '../services/authorizeNetDirect';

const Checkout = () => {
  const navigate = useNavigate();
  const {
    items, subtotal, taxAmount, shippingCost, total, clearCart,
    FLAT_RATE_SHIPPING, TX_SALES_TAX
  } = useCart();

  const [step, setStep] = useState(1); // 1: Info, 2: Payment, 3: Success
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [shippingMethod, setShippingMethod] = useState('shipping');

  const [customerInfo, setCustomerInfo] = useState({
    name: '', email: '', phone: '',
    address: '', city: '', state: 'TX', zip: ''
  });

  const [cardInfo, setCardInfo] = useState({
    number: '', expiry: '', cvv: ''
  });

  // Recalculate with pickup option
  const actualShipping = shippingMethod === 'pickup' ? 0 : FLAT_RATE_SHIPPING;
  const actualTotal = Math.round((subtotal + taxAmount + actualShipping) * 100) / 100;

  const handleCustomerSubmit = (e) => {
    e.preventDefault();
    if (!customerInfo.name || !customerInfo.email) {
      setError('Name and email are required.');
      return;
    }
    if (shippingMethod === 'shipping' && (!customerInfo.address || !customerInfo.city || !customerInfo.zip)) {
      setError('Shipping address is required.');
      return;
    }
    setError('');
    setStep(2);
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    setError('');
    setProcessing(true);

    try {
      // Format card expiry
      const rawExpiry = cardInfo.expiry.replace(/\D/g, '');
      const expirationDate = rawExpiry.length === 4
        ? `${rawExpiry.substring(0, 2)}${rawExpiry.substring(2, 4)}`
        : rawExpiry;

      // Process payment via Authorize.Net
      const paymentResult = await authorizeNetDirect.processPayment({
        amount: actualTotal,
        cardNumber: cardInfo.number.replace(/\s/g, ''),
        expirationDate,
        cvv: cardInfo.cvv,
        customer: {
          name: customerInfo.name,
          firstName: customerInfo.name.split(' ')[0],
          lastName: customerInfo.name.split(' ').slice(1).join(' ') || customerInfo.name,
          email: customerInfo.email,
          phone: customerInfo.phone,
          address: customerInfo.address,
          city: customerInfo.city,
          state: customerInfo.state,
          zip: customerInfo.zip
        }
      });

      if (!paymentResult.success) {
        throw new Error(paymentResult.error || 'Payment failed. Check your card info and try again.');
      }

      // Create order record
      const orderItems = items.map(item => ({
        product_id: item.product_id,
        name: item.name,
        quantity: item.quantity,
        price: item.price
      }));

      const { data: order, error: orderError } = await partsStoreService.createOrder({
        customer_name: customerInfo.name,
        customer_email: customerInfo.email,
        customer_phone: customerInfo.phone,
        shipping_address: shippingMethod === 'shipping' ? {
          address: customerInfo.address,
          city: customerInfo.city,
          state: customerInfo.state,
          zip: customerInfo.zip
        } : null,
        shipping_method: shippingMethod,
        items: orderItems,
        subtotal,
        tax_amount: taxAmount,
        shipping_cost: actualShipping,
        total: actualTotal,
        payment_status: 'paid',
        transaction_id: paymentResult.transactionId
      });

      if (orderError) {
        console.error('Order creation error (payment was successful):', orderError);
      }

      setOrderNumber(order?.order_number || 'Confirmed');
      clearCart();
      setStep(3);

    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message || 'Payment processing failed. Try again.');
    }

    setProcessing(false);
  };

  const formatCardNumber = (value) => {
    const v = value.replace(/\D/g, '').substring(0, 16);
    return v.replace(/(.{4})/g, '$1 ').trim();
  };

  const formatExpiry = (value) => {
    const v = value.replace(/\D/g, '').substring(0, 4);
    if (v.length >= 3) return `${v.substring(0, 2)}/${v.substring(2)}`;
    return v;
  };

  const formatPrice = (p) => `$${parseFloat(p).toFixed(2)}`;

  // Redirect if cart is empty (unless on success screen)
  if (items.length === 0 && step !== 3) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Nothing to check out</h1>
          <Link to="/parts" className="text-blue-600 hover:underline font-medium">Browse the parts store →</Link>
        </div>
      </div>
    );
  }

  // Success screen
  if (step === 3) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-xl p-8 md:p-12 max-w-md w-full text-center"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <FaCheck className="text-3xl text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Order Placed!</h1>
          <p className="text-gray-500 mb-1">Order #{orderNumber}</p>
          <p className="text-gray-500 mb-6">A confirmation has been sent to {customerInfo.email}.</p>
          {shippingMethod === 'pickup' && (
            <div className="bg-blue-50 rounded-xl p-4 mb-6 text-sm text-blue-800">
              <FaStore className="inline mr-2" />
              Pick up at Paradise Performance, Paradise TX. We'll contact you when your order is ready.
            </div>
          )}
          <Link
            to="/parts"
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700"
          >
            Continue Shopping
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-8 md:py-12">
        {/* Breadcrumb */}
        <Link to="/cart" className="inline-flex items-center gap-2 text-gray-500 hover:text-blue-600 text-sm mb-6">
          <FaArrowLeft /> Back to cart
        </Link>

        {/* Progress */}
        <div className="flex items-center gap-4 mb-10">
          <div className={`flex items-center gap-2 ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>1</span>
            <span className="text-sm font-medium hidden sm:inline">Information</span>
          </div>
          <div className={`flex-1 h-0.5 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`} />
          <div className={`flex items-center gap-2 ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>2</span>
            <span className="text-sm font-medium hidden sm:inline">Payment</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Form Area */}
          <div className="lg:col-span-3">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-6 text-sm">
                {error}
              </div>
            )}

            {/* Step 1: Customer Info */}
            {step === 1 && (
              <motion.form
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                onSubmit={handleCustomerSubmit}
                className="bg-white rounded-2xl border border-gray-200 p-6 space-y-5"
              >
                <h2 className="text-lg font-bold text-gray-900">Contact Information</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                    <input
                      type="text"
                      required
                      value={customerInfo.name}
                      onChange={(e) => setCustomerInfo(p => ({ ...p, name: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input
                      type="email"
                      required
                      value={customerInfo.email}
                      onChange={(e) => setCustomerInfo(p => ({ ...p, email: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={customerInfo.phone}
                    onChange={(e) => setCustomerInfo(p => ({ ...p, phone: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Shipping Method */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Delivery Method</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setShippingMethod('shipping')}
                      className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all text-left ${
                        shippingMethod === 'shipping'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <FaTruck className={shippingMethod === 'shipping' ? 'text-blue-600' : 'text-gray-400'} />
                      <div>
                        <p className="text-sm font-medium">Ship It</p>
                        <p className="text-xs text-gray-500">${FLAT_RATE_SHIPPING} flat rate</p>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setShippingMethod('pickup')}
                      className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all text-left ${
                        shippingMethod === 'pickup'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <FaStore className={shippingMethod === 'pickup' ? 'text-blue-600' : 'text-gray-400'} />
                      <div>
                        <p className="text-sm font-medium">Pickup</p>
                        <p className="text-xs text-gray-500">Free — Paradise, TX</p>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Shipping Address */}
                {shippingMethod === 'shipping' && (
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-700">Shipping Address</h3>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Street Address *</label>
                      <input
                        type="text"
                        required
                        value={customerInfo.address}
                        onChange={(e) => setCustomerInfo(p => ({ ...p, address: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">City *</label>
                        <input
                          type="text"
                          required
                          value={customerInfo.city}
                          onChange={(e) => setCustomerInfo(p => ({ ...p, city: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">State</label>
                        <input
                          type="text"
                          value={customerInfo.state}
                          onChange={(e) => setCustomerInfo(p => ({ ...p, state: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">ZIP *</label>
                        <input
                          type="text"
                          required
                          value={customerInfo.zip}
                          onChange={(e) => setCustomerInfo(p => ({ ...p, zip: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full bg-blue-600 text-white py-3.5 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
                >
                  Continue to Payment
                </button>
              </motion.form>
            )}

            {/* Step 2: Payment */}
            {step === 2 && (
              <motion.form
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                onSubmit={handlePayment}
                className="bg-white rounded-2xl border border-gray-200 p-6 space-y-5"
              >
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold text-gray-900">Payment</h2>
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    ← Edit Info
                  </button>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 text-sm">
                  <p className="font-medium">{customerInfo.name}</p>
                  <p className="text-gray-500">{customerInfo.email}</p>
                  {shippingMethod === 'shipping' && (
                    <p className="text-gray-500">{customerInfo.address}, {customerInfo.city} {customerInfo.state} {customerInfo.zip}</p>
                  )}
                  {shippingMethod === 'pickup' && (
                    <p className="text-blue-600 font-medium mt-1">📍 Local Pickup — Paradise, TX</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Card Number</label>
                  <div className="relative">
                    <FaCreditCard className="absolute left-3 top-3 text-gray-400" />
                    <input
                      type="text"
                      required
                      placeholder="1234 5678 9012 3456"
                      value={cardInfo.number}
                      onChange={(e) => setCardInfo(p => ({ ...p, number: formatCardNumber(e.target.value) }))}
                      maxLength={19}
                      className="w-full border border-gray-300 rounded-lg pl-10 pr-3 py-2.5 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Expiry</label>
                    <input
                      type="text"
                      required
                      placeholder="MM/YY"
                      value={cardInfo.expiry}
                      onChange={(e) => setCardInfo(p => ({ ...p, expiry: formatExpiry(e.target.value) }))}
                      maxLength={5}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">CVV</label>
                    <input
                      type="text"
                      required
                      placeholder="123"
                      value={cardInfo.cvv}
                      onChange={(e) => setCardInfo(p => ({ ...p, cvv: e.target.value.replace(/\D/g, '').substring(0, 4) }))}
                      maxLength={4}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={processing}
                  className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-3.5 rounded-xl font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {processing ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      Processing...
                    </span>
                  ) : (
                    <><FaLock /> Pay {formatPrice(actualTotal)}</>
                  )}
                </button>

                <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
                  <FaShieldAlt />
                  <span>Encrypted & secure. Powered by Authorize.Net.</span>
                </div>
              </motion.form>
            )}
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-gray-200 p-6 sticky top-24">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Order Summary</h2>

              <div className="space-y-3 mb-6">
                {items.map(item => (
                  <div key={item.product_id} className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 relative">
                      {item.image ? (
                        <img src={item.image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-300 text-lg">🔧</div>
                      )}
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-gray-600 text-white text-xs rounded-full flex items-center justify-center">
                        {item.quantity}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.name}</p>
                    </div>
                    <p className="text-sm font-medium">{formatPrice(item.price * item.quantity)}</p>
                  </div>
                ))}
              </div>

              <div className="space-y-2 text-sm border-t pt-4">
                <div className="flex justify-between">
                  <span className="text-gray-500">Subtotal</span>
                  <span>{formatPrice(subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Shipping</span>
                  <span>{shippingMethod === 'pickup' ? 'Free' : formatPrice(FLAT_RATE_SHIPPING)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Tax</span>
                  <span>{formatPrice(taxAmount)}</span>
                </div>
                <div className="flex justify-between text-base font-bold border-t pt-2">
                  <span>Total</span>
                  <span>{formatPrice(actualTotal)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
